import { cn } from "@/lib/utils";
import type { ITradingCompetitionPrizeBand } from "@/sanity/pages/PageTradingCompetitionTemplate";

/** Tab look per band, in order: metal gradients for the podium, then the greens. */
const TABS = [
  { bg: "linear-gradient(140.19deg, #f6e3a1 0%, #d2aa4e 37.037%, #f3e0a8 74.074%)", text: "#3d2c05", line: "#c9a24a" },
  { bg: "linear-gradient(141.89deg, #f4f6f7 0%, #b9c0c5 37.037%, #e9ecee 74.074%)", text: "#2b3336", line: "#aeb6bc" },
  { bg: "linear-gradient(141.89deg, #f2c9a2 0%, #bd7a47 37.037%, #edbb8e 74.074%)", text: "#3a1f0c", line: "#b87444" },
  { bg: "#0e5b44", text: "#ffffff", line: "#0e5b44" },
  { bg: "#1b8869", text: "#ffffff", line: "#1b8869" },
  { bg: "#7ccb9e", text: "#001b14", line: "#7ccb9e" },
  { bg: "#a3deb2", text: "#001b14", line: "#a3deb2" },
  { bg: "#cdebdb", text: "#001b14", line: "#cdebdb" },
];

/** Desktop body sizes from Figma (the podium bands are shorter: no winners line). */
const DESKTOP_BODY = [
  { w: 31.25, h: 16.25 },
  { w: 31, h: 16.5 },
  { w: 31, h: 16.5 },
];
const DESKTOP_BODY_DEFAULT = { w: 31, h: 19.75 };

interface Props {
  band: ITradingCompetitionPrizeBand;
  index: number;
  variant: "desktop" | "mobile";
  cashLabel?: string;
  voucherLabel?: string;
  winnersText?: string;
}

export default function PrizeCard({ band, index, variant, cashLabel, voucherLabel, winnersText }: Props) {
  const tab = TABS[index % TABS.length];
  const winners = band.winners ?? 1;
  const winnersLine = winners > 1 && winnersText ? winnersText.replace("{n}", String(winners)) : undefined;
  const voucher = band.payout === "voucher";
  const amount = `$${(band.amount ?? 0).toLocaleString("en-US")}`;
  const desktop = variant === "desktop";
  const body = DESKTOP_BODY[index] ?? DESKTOP_BODY_DEFAULT;

  const badge = (
    <div
      className={cn(
        "flex shrink-0 items-start overflow-hidden",
        desktop ? "rounded-[0.25rem] px-[1rem] py-[0.25rem]" : "rounded-[0.241rem] px-[0.965rem] py-[0.241rem]",
        voucher ? "bg-[#a3deb2]" : "bg-[#003828]",
      )}>
      <p
        className={cn(
          "mb-0 font-geist font-semibold whitespace-nowrap",
          desktop ? "text-[1.75rem] leading-[normal]" : "text-[1.689rem] leading-[normal]",
          voucher ? "text-[#003828]" : "text-white",
        )}>
        {voucher ? voucherLabel : cashLabel}
      </p>
    </div>
  );

  return (
    <div className="flex w-full flex-col items-start">
      <div
        className={cn(
          "flex shrink-0 items-start overflow-hidden",
          desktop ? "rounded-t-[0.5rem] px-[1.75rem] py-[0.75rem]" : "rounded-t-[0.4825rem] px-[1.689rem] py-[0.724rem]",
        )}
        style={{ background: tab.bg }}>
        <p
          className={cn(
            "mb-0 font-geist font-medium whitespace-nowrap",
            desktop ? "text-[1.5rem] leading-[normal] tracking-[0.12rem]" : "text-[1.4475rem] leading-[normal] tracking-[0.1158rem]",
          )}
          style={{ color: tab.text }}>
          {band.label}
        </p>
      </div>

      <div
        className={cn(
          "relative flex overflow-hidden rounded-tr-[0.5rem] rounded-b-[0.5rem] border border-solid border-[rgba(0,27,20,0.06)] bg-[#f2f4f3]",
          desktop
            ? "flex-col items-start justify-between p-[2.25rem]"
            : cn("w-full flex-col items-center justify-center px-[2.5rem] pt-[2.5rem] pb-[2.25rem]", index < 3 && "h-[12.125rem]"),
        )}
        style={desktop ? { width: `${body.w}rem`, height: `${body.h}rem` } : undefined}>
        {desktop ? (
          <>
            <p className="mb-0 font-geist text-[4rem] leading-[normal] font-medium whitespace-nowrap text-[#001b14]">{amount}</p>
            {winnersLine && (
              <p className="mb-0 font-geist text-[1.875rem] leading-[normal] font-normal whitespace-nowrap text-[rgba(0,27,20,0.6)]">
                {winnersLine}
              </p>
            )}
            <i aria-hidden="true" className="h-[0.75rem] w-px shrink-0" />
            {badge}
          </>
        ) : (
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-[0.5rem] leading-[normal] whitespace-nowrap">
              <p className="mb-0 font-geist text-[3.3775rem] leading-[normal] font-medium text-[#001b14]">{amount}</p>
              {winnersLine && (
                <p className="mb-0 font-geist text-[1.689rem] leading-[normal] font-normal text-[rgba(0,27,20,0.6)]">{winnersLine}</p>
              )}
            </div>
            {badge}
          </div>
        )}
        <i
          aria-hidden="true"
          className={cn("absolute top-[-1px] right-[-1px] left-[-1px]", desktop ? "h-[0.1875rem]" : "h-[0.181rem]")}
          style={{ background: tab.line }}
        />
      </div>
    </div>
  );
}
